import {Decoration, EditorView, keymap, lineNumbers} from '@codemirror/view';
import {Compartment, EditorState, StateEffect, StateField} from '@codemirror/state';
import {
    bracketMatching,
    foldEffect,
    foldGutter,
    foldKeymap,
    foldedRanges,
    indentOnInput,
    syntaxHighlighting,
    unfoldEffect,
    defaultHighlightStyle,
} from '@codemirror/language';
import {autocompletion, completionKeymap} from '@codemirror/autocomplete';
import {defaultKeymap, history, historyKeymap} from '@codemirror/commands';
import {html} from '@codemirror/lang-html';

const instances = new WeakMap();

const HOST_CLASS = 'code-editor-host';

const EVENTS = ['change', 'selectionChange', 'scroll', 'focus', 'blur'];

const FOLD_STORAGE_PREFIX = 'codeEditor:folds:';
const FOLD_PERSIST_DELAY  = 250;

const errorLineMark = Decoration.line({class: 'cm-error-line'});

const setErrorLineEffect = StateEffect.define();

const errorLineField = StateField.define({
    create: () => Decoration.none,
    update(decorations, transaction) {
        let next = decorations.map(transaction.changes);

        for (const effect of transaction.effects) {
            if (!effect.is(setErrorLineEffect)) {
                continue;
            }

            const line = effect.value;

            if (line === null) {
                next = Decoration.none;
                continue;
            }

            const doc = transaction.state.doc;

            if (line < 1 || line > doc.lines) {
                next = Decoration.none;
                continue;
            }

            next = Decoration.set([errorLineMark.range(doc.line(line).from)]);
        }

        return next;
    },
    provide: field => EditorView.decorations.from(field),
});

const MAX_UTF8_BYTES_PER_CODE_UNIT = 3;

const limitDocumentBytes = maxBytes => EditorState.transactionFilter.of(transaction => {
    if (!transaction.docChanged) {
        return transaction;
    }

    const codeUnits = transaction.newDoc.length;

    if (codeUnits > maxBytes) {
        return [];
    }

    if (codeUnits * MAX_UTF8_BYTES_PER_CODE_UNIT <= maxBytes) {
        return transaction;
    }

    return new TextEncoder().encode(transaction.newDoc.toString()).length > maxBytes ? [] : transaction;
});

class CodeEditorInstance {
    constructor(textarea) {
        this.textarea  = textarea;
        this.host      = document.createElement('div');
        this.host.className = HOST_CLASS;
        this.storageKey  = textarea.dataset.storageKey
            ? `${FOLD_STORAGE_PREFIX}${textarea.dataset.storageKey}`
            : null;
        this._listeners  = new Map(EVENTS.map(name => [name, new Set()]));
        this._extensions = new Compartment();
        this._persistTimer = null;

        textarea.parentNode.insertBefore(this.host, textarea);
        textarea.classList.add('code-editor-source');

        const maxBytes = parseInt(textarea.dataset.maxBytes, 10) || 0;

        this.view = new EditorView({
            parent: this.host,
            state: EditorState.create({
                doc: textarea.value,
                extensions: [
                    lineNumbers(),
                    foldGutter(),
                    history(),
                    indentOnInput(),
                    bracketMatching(),
                    autocompletion(),
                    syntaxHighlighting(defaultHighlightStyle, {fallback: true}),
                    keymap.of([
                        ...defaultKeymap,
                        ...historyKeymap,
                        ...foldKeymap,
                        ...completionKeymap,
                    ]),
                    html({autoCloseTags: false, matchClosingTags: true}),
                    EditorView.lineWrapping,
                    errorLineField,
                    ...(maxBytes > 0 ? [limitDocumentBytes(maxBytes)] : []),
                    EditorView.updateListener.of(update => this._onViewUpdate(update)),
                    EditorView.domEventHandlers({
                        scroll: () => this._emit('scroll'),
                        focus:  () => this._emit('focus'),
                        blur:   () => this._emit('blur'),
                    }),
                    this._extensions.of([]),
                ],
            }),
        });

        if (textarea.form) {
            textarea.form.addEventListener('submit', () => {
                textarea.value = this.value;
            });
        }

        this._restoreFolds();
    }

    _restoreFolds() {
        if (!this.storageKey || typeof localStorage === 'undefined') {
            return;
        }

        let ranges;

        try {
            const raw = localStorage.getItem(this.storageKey);

            if (!raw) {
                return;
            }

            ranges = JSON.parse(raw);
        } catch {
            return;
        }

        if (!Array.isArray(ranges)) {
            return;
        }

        const docLength = this.view.state.doc.length;
        const effects   = ranges
            .filter(range => Number.isInteger(range.from)
                && Number.isInteger(range.to)
                && range.from >= 0
                && range.to <= docLength
                && range.from < range.to)
            .map(range => foldEffect.of({from: range.from, to: range.to}));

        if (effects.length === 0) {
            return;
        }

        this.view.dispatch({effects});
    }

    _persistFolds() {
        if (!this.storageKey || typeof localStorage === 'undefined') {
            return;
        }

        const ranges = [];

        foldedRanges(this.view.state).between(0, this.view.state.doc.length, (from, to) => {
            ranges.push({from, to});
        });

        try {
            if (ranges.length === 0) {
                localStorage.removeItem(this.storageKey);
            } else {
                localStorage.setItem(this.storageKey, JSON.stringify(ranges));
            }
        } catch {
            // localStorage may be unavailable (private mode, quota, etc.) — silent fail.
        }
    }

    _scheduleFoldPersist() {
        if (!this.storageKey) {
            return;
        }

        clearTimeout(this._persistTimer);
        this._persistTimer = setTimeout(() => this._persistFolds(), FOLD_PERSIST_DELAY);
    }

    _onViewUpdate(update) {
        if (update.docChanged) {
            const value = update.state.doc.toString();

            if (this.textarea.value !== value) {
                this.textarea.value = value;
                this.textarea.dispatchEvent(new Event('input', {bubbles: true}));
            }

            this._emit('change');
        }

        if (update.selectionSet) {
            this._emit('selectionChange');
        }

        if (this._foldsChanged(update)) {
            this._scheduleFoldPersist();
        }
    }

    _foldsChanged(update) {
        if (update.docChanged) {
            return true;
        }

        for (const transaction of update.transactions) {
            for (const effect of transaction.effects) {
                if (effect.is(foldEffect) || effect.is(unfoldEffect)) {
                    return true;
                }
            }
        }

        return false;
    }

    _emit(event) {
        const handlers = this._listeners.get(event);

        if (!handlers) {
            return;
        }

        for (const handler of handlers) {
            handler();
        }
    }

    on(event, handler) {
        const handlers = this._listeners.get(event);

        if (!handlers) {
            throw new Error(`CodeEditor: unknown event "${event}"`);
        }

        handlers.add(handler);

        return () => handlers.delete(handler);
    }

    reconfigure(extension) {
        this.view.dispatch({effects: this._extensions.reconfigure(extension)});
    }

    get value() {
        return this.view.state.doc.toString();
    }

    set value(next) {
        const current = this.value;

        if (next === current) {
            return;
        }

        this.view.dispatch({
            changes: {from: 0, to: current.length, insert: next},
        });
    }

    getSelection() {
        const main = this.view.state.selection.main;

        return {start: main.from, end: main.to};
    }

    setSelection(start, end) {
        this.view.dispatch({selection: {anchor: start, head: end}});
    }

    replaceRange(start, end, text) {
        this.view.dispatch({
            changes:   {from: start, to: end, insert: text},
            selection: {anchor: start + text.length},
        });

        return {start, end: start + text.length};
    }

    coordsAtPos(pos) {
        return this.view.coordsAtPos(pos);
    }

    // Highlight a 1-based line as the source of an error, or pass `null` to
    // clear. Out-of-range values are treated as a clear so callers don't
    // have to guard themselves.
    setErrorLine(line) {
        const next = Number.isFinite(line) && line >= 1 ? Math.floor(line) : null;
        this.view.dispatch({effects: setErrorLineEffect.of(next)});
    }

    focus() {
        this.view.focus();
    }

    destroy() {
        this.view.destroy();
        this.host.remove();
        this.textarea.classList.remove('code-editor-source');
    }
}

const discardDeadHost = textarea => {
    const previous = textarea.previousElementSibling;

    if (previous?.classList.contains(HOST_CLASS)) {
        previous.remove();
    }
};

const CodeEditor = {
    attach(textarea) {
        if (!(textarea instanceof HTMLTextAreaElement)) {
            throw new Error('CodeEditor.attach: target must be a <textarea>');
        }

        const existing = instances.get(textarea);

        if (existing) {
            return existing;
        }

        discardDeadHost(textarea);

        const instance = new CodeEditorInstance(textarea);
        instances.set(textarea, instance);

        return instance;
    },

    get(textarea) {
        return instances.get(textarea) ?? null;
    },
};

export default CodeEditor;
export {CodeEditor};
